import React, { useState } from "react";
import { motion } from "framer-motion";

/**
 * UrlAnalyzer component
 * Allows users to enter a website URL for analysis
 */
const UrlAnalyzer = ({
  onAnalyze,
  isAnalyzing,
  analysisResults,
  analyzeError,
  websiteUrl = "",
  inTimelineMode = false,
}) => {
  const [url, setUrl] = useState(websiteUrl || "");
  const [error, setError] = useState("");

  // Handle URL analysis
  const handleAnalyze = (e) => {
    e.preventDefault();

    // Basic URL validation
    if (!url.trim()) {
      setError("Please enter a URL");
      return;
    }

    // Make sure URL has http:// or https://
    let validUrl = url;
    if (!/^https?:\/\//i.test(url)) {
      validUrl = `https://${url}`;
      setUrl(validUrl);
    }

    setError("");
    onAnalyze(validUrl);
  };

  return (
    <div className={`w-full ${inTimelineMode ? "" : "max-w-3xl mx-auto"}`}>
      <div className="bg-neutral-800/60 backdrop-blur-sm border border-neutral-700 rounded-xl p-5 md:p-8">
        <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
          Analyze Your Website
        </h3>

        <p className="text-neutral-400 mb-6">
          Enter your website URL to automatically extract brand colors, fonts,
          and other information to help customize your experience.
        </p>

        {/* URL input form */}
        <form onSubmit={handleAnalyze} className="mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
              disabled={isAnalyzing}
            />

            <button
              type="submit"
              disabled={isAnalyzing}
              className={`px-6 py-3 rounded-lg font-medium flex items-center justify-center whitespace-nowrap ${
                isAnalyzing
                  ? "bg-neutral-700 text-neutral-300 cursor-not-allowed"
                  : "bg-primary-green text-black hover:bg-primary-green/90"
              } transition-colors`}
            >
              {isAnalyzing ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-black/50"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Analyzing...
                </>
              ) : (
                "Analyze Website"
              )}
            </button>
          </div>

          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}

          {analyzeError && (
            <p className="mt-2 text-sm text-red-500">Error: {analyzeError}</p>
          )}
        </form>

        {/* Analysis results */}
        {analysisResults && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-neutral-900 border border-neutral-700 rounded-lg p-5"
          >
            <h4 className="text-lg font-medium text-white mb-4">
              Analysis Results
            </h4>

            {/* Colors */}
            {analysisResults.colors && analysisResults.colors.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-neutral-400 mb-2">
                  Detected Colors:
                </p>
                <div className="flex flex-wrap gap-2">
                  {analysisResults.colors.map((color, index) => (
                    <div key={index} className="flex flex-col items-center">
                      <div
                        className="h-8 w-8 rounded-full border border-neutral-700"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-xs text-neutral-500 mt-1">
                        {color}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fonts */}
            {analysisResults.fonts && analysisResults.fonts.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-neutral-400 mb-2">Detected Fonts:</p>
                <div className="flex flex-wrap gap-2">
                  {analysisResults.fonts.map((font, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-neutral-800 text-neutral-300 text-sm rounded"
                    >
                      {font}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Company Details */}
            {analysisResults.companyDetails && (
              <div>
                <p className="text-sm text-neutral-400 mb-2">
                  Company Details:
                </p>
                <div className="bg-neutral-800 rounded p-3">
                  {analysisResults.companyDetails.name && (
                    <p className="text-white">
                      <strong>Name:</strong>{" "}
                      {analysisResults.companyDetails.name}
                    </p>
                  )}
                  {analysisResults.companyDetails.industry && (
                    <p className="text-white">
                      <strong>Industry:</strong>{" "}
                      {analysisResults.companyDetails.industry}
                    </p>
                  )}
                  {analysisResults.companyDetails.description && (
                    <p className="text-white">
                      <strong>Description:</strong>{" "}
                      {analysisResults.companyDetails.description}
                    </p>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default UrlAnalyzer;
